import { forwardRef } from "react";

import cn from "@/helpers/classNames";

import { ChevronsUpDownIcon, ChevronUpIcon } from "lucide-react";

import { Button } from "../Button";

const Table = forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>((props, ref) => {
  return (
    <div
      className={cn(
        "relative w-full animate-table-container-scroll-shadow overflow-x-auto",
        props.className,
      )}
      style={{ animationTimeline: "scroll(self inline)" }}
    >
      <table
        ref={ref}
        {...props}
        className={cn("w-full border-b text-xs sm:text-sm", props.className)}
      />
    </div>
  );
});
Table.displayName = "Table";

const TableHeader = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&>tr]:border-y", className)} {...props} />
));
TableHeader.displayName = "TableHeader";

const TableBody = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn(
      "text-gray-700 dark:text-gray-100 [&_tr:last-child]:border-0",
      className,
    )}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableFooter = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-black/5 font-medium dark:bg-white/5 last:[&>tr]:border-b-0",
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => {
  return (
    <tr
      ref={ref}
      className={cn(
        "border-b",
        {
          "transition-colors [&:hover:not(:has(td.no-row-hover:hover))]:bg-gray-100 dark:[&:hover:not(:has(td.no-row-hover:hover))]:bg-white/5 [&:not(:has(td.no-row-hover:hover))]:cursor-pointer":
            props.onClick !== undefined,
        },
        className,
      )}
      {...props}
    />
  );
});
TableRow.displayName = "TableRow";

const TableHead = forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    scope="col"
    className={cn(
      "px-2 py-1 text-left align-middle font-medium text-foreground",
      className,
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

function TableHeadContent(
  props: React.HTMLAttributes<HTMLDivElement> & {
    isSorted?: boolean;
    isSortedDesc?: boolean;
    canSort?: boolean;
    onClick?: () => void;
    children?: React.ReactNode;
  },
) {
  if (props.canSort && props.onClick) {
    return (
      <Button
        variant="ghost"
        size="sm"
        iconRight={
          props.isSorted ? (
            <ChevronUpIcon
              className={cn({
                "rotate-180 transition-transform": props.isSortedDesc,
              })}
            />
          ) : (
            <ChevronsUpDownIcon className="opacity-40" />
          )
        }
        onClick={props.onClick}
        className="-mx-2 h-7 px-2 text-xs whitespace-nowrap focus:ring-0 focus:ring-offset-0 focus:outline-hidden sm:text-sm"
      >
        {props.children}
      </Button>
    );
  }
  return (
    <span className="font-medium whitespace-nowrap">{props.children}</span>
  );
}

const TableCell = forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td ref={ref} className={cn("p-2 align-middle", className)} {...props} />
));
TableCell.displayName = "TableCell";

export {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeadContent,
  TableHeader,
  TableRow,
};
